// TransactionPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { accountApi, transactionApi, transactionTypeApi, securityApi, counterpartyApi, API_BASE_URL } from '../services/api';
import { formatPrice } from '../utils/formatPrice';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';

import './TransactionPage.css';
import '../calendar-custom.css'; // Import custom calendar styling


function TransactionPage({ allowedTabs }) {
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user'));
  // Support all authorizer roles
  const AUTHORIZER_ROLES = ['authorizer', 'front_office', 'back_office_verifier', 'back_office_final'];
  const isAuthorizer = user && AUTHORIZER_ROLES.includes(user.role);
  const loggedInUsername = user?.username || '';

  // State for form fields
  const [transactionData, setTransactionData] = useState({
    tradeDate: format(new Date(), 'yyyy-MM-dd'),
    valueDate: format(new Date(), 'yyyy-MM-dd'),
    security: '',
    sourceAccount: '',
    amount: '',
    interestRate: '',
    counterparty: '',
    transactionType: '',
    settlementMode: 'RTGS', // Default value
    price: '',
    yield: '',
    description: '',
    portfolio: 'AFS', // Default value
    strategy: '',
    currency: 'LKR', // Default value
    transactionCode: '',
    commission: '',
    brokerage: '',
    remarks: ''
  });



  // State for details modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);



  // State for authorization
  const [comment, setComment] = useState('');

  // State for dropdown options - initialize as empty arrays to prevent map errors
  const [sourceAccounts, setSourceAccounts] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [securities, setSecurities] = useState([]); // Add state for securities
  const [counterparties, setCounterparties] = useState([]);
  const [strategies, setStrategies] = useState([]); // Add state for strategies

  // Loading states for dropdowns
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingSecurities, setLoadingSecurities] = useState(false);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);

  // Memoized dropdown options for performance
  const memoAccounts = useMemo(() => sourceAccounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>), [sourceAccounts]);
  const memoTypes = useMemo(() => transactionTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>), [transactionTypes]);
  const memoSecurities = useMemo(() => securities.map(sec => <option key={sec.id} value={sec.id}>{sec.name}</option>), [securities]);
  const memoCounterparties = useMemo(() => counterparties.map(cp => (
    <option key={cp.unique_id} value={cp.unique_id}>
      {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
    </option>
  )), [counterparties]);

  // Settlement Mode options
  const settlementModes = [
    { id: 'RTGS', name: 'RTGS' },
    { id: 'CEFT', name: 'CEFT' },
    { id: 'SLIPS', name: 'SLIPS' },
    { id: 'Cheque', name: 'Cheque' },
    { id: 'Other', name: 'Other' }
  ];

  // Portfolio options (fetched from backend)
  const [portfolioOptions, setPortfolioOptions] = useState([]);

  // Fetch portfolios and strategies from backend
  useEffect(() => {
    // Fetch portfolios
    fetch(`${API_BASE_URL}/portfolio-master`)
      .then(res => res.json())
      .then(data => {
        setPortfolioOptions(data.map(p => ({ id: p.portfolio_id, name: p.portfolio_name })));
      })
      .catch(() => setPortfolioOptions([]));

    // Fetch strategies
    fetch(`${API_BASE_URL}/strategy-master`)
      .then(res => res.json())
      .then(data => {
        setStrategies(data.map(s => ({ id: s.strategy_id, name: s.strategy_id + (s.strategy_type ? ` (${s.strategy_type})` : '') })));
      })
      .catch(() => setStrategies([]));
  }, []);

  // Currency options
  const currencyOptions = [
    { id: 'LKR', name: 'LKR' },
    { id: 'USD', name: 'USD' },
    { id: 'EUR', name: 'EUR' },
    { id: 'CAD', name: 'CAD' },
    { id: 'GBP', name: 'GBP' }
  ];

  // State for recent transactions
  const [recentTransactions, setRecentTransactions] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editTransaction, setEditTransaction] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createdTransactionId, setCreatedTransactionId] = useState(null);
  const [showDocumentSection, setShowDocumentSection] = useState(false);

  // Mock data for when API calls fail
  const mockSecurities = [
    { id: 1, name: 'Treasury Bond 2023 10.5%' },
    { id: 2, name: 'Treasury Bond 2025 11.25%' },
    { id: 3, name: 'Corporate Bond XYZ Ltd 12%' },
    { id: 4, name: 'Treasury Bill 91 Days' },
    { id: 5, name: 'Treasury Bill 182 Days' },
    { id: 6, name: 'Treasury Bill 364 Days' },
    { id: 7, name: 'Fixed Deposit - Bank ABC' }
  ];

  const mockAccounts = [
    { id: 1, name: 'Primary Trading Account' },
    { id: 2, name: 'Secondary Portfolio' },
    { id: 3, name: 'Corporate Investment Fund' },
    { id: 4, name: 'Fixed Income Reserve' },
    { id: 5, name: 'Treasury Operations' }
  ];

  const mockTransactionTypes = [
    { id: 1, name: 'Buy' },
    { id: 2, name: 'Sell' },
    { id: 3, name: 'Coupon Payment' },
    { id: 4, name: 'Maturity' },
    { id: 5, name: 'Transfer' }
  ];

  const mockCounterparties = [
    { id: 1, name: 'Central Bank' },
    { id: 2, name: 'Bank of Ceylon' },
    { id: 3, name: 'Commercial Bank' },
    { id: 4, name: 'Sampath Bank' },
    { id: 5, name: 'Hatton National Bank' },
    { id: 6, name: 'NSB' },
    { id: 7, name: 'Union Bank' }
  ];

  // Helper for caching with expiry
  function getCachedOrFetch(key, fetchFn, setState, setLoading, fallback = [], expiryMin = 60) {
    setLoading(true);
    const cache = localStorage.getItem(key);
    if (cache) {
      const { data, timestamp } = JSON.parse(cache);
      if (Date.now() - timestamp < expiryMin * 60 * 1000) {
        setState(data);
        setLoading(false);
        return;
      }
    }
    fetchFn()
      .then(data => {
        setState(data);
        localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
      })
      .catch(err => {
        console.error(`Error fetching ${key}:`, err);
        setState(fallback);
      })
      .finally(() => setLoading(false));
  }

  // Fetch initial data (accounts, transaction types, securities, counterparties)
  useEffect(() => {
    getCachedOrFetch('accounts_cache', accountApi.getAll, setSourceAccounts, setLoadingAccounts, mockAccounts);
    getCachedOrFetch('types_cache', transactionTypeApi.getAll, setTransactionTypes, setLoadingTypes, mockTransactionTypes);
    getCachedOrFetch('securities_cache', securityApi.getAll, setSecurities, setLoadingSecurities, mockSecurities);
    getCachedOrFetch('counterparties_cache', counterpartyApi.getAll, setCounterparties, setLoadingCounterparties, mockCounterparties);
  }, []);

  // Load transactions on component mount
  useEffect(() => {
    fetchTransactions();
  }, [isAuthorizer]);

  // Function to fetch transactions
  // Mock transactions for when API fails
  const mockTransactions = [
    {
      id: 1,
      dealNumber: 'TRX00123',
      tradeDate: '2025-05-28T09:30:00',
      settlementDate: '2025-05-30T15:00:00',
      security: 'Treasury Bond 2023 10.5%',
      securityId: 1,
      amount: 1000000,
      price: 102.5000,
      status: 'Pending',
      transactionCode: 'PUR',
      commission: 500.0000,
      brokerage: 250.0000,
      description: 'Primary market purchase',
      remarks: 'Standard settlement cycle'
    },
    {
      id: 2,
      dealNumber: 'TRX00124',
      tradeDate: '2025-05-27T10:15:00',
      settlementDate: '2025-05-29T15:00:00',
      security: 'Treasury Bill 91 Days',
      securityId: 4,
      amount: 500000,
      price: 98.7500,
      status: 'Approved',
      transactionCode: 'PUR',
      commission: 250.0000,
      brokerage: 125.0000,
      description: 'Secondary market trade',
      remarks: 'Approved by treasury department'
    },
    {
      id: 3,
      dealNumber: 'TRX00125',
      tradeDate: '2025-05-26T11:00:00',
      settlementDate: '2025-05-28T15:00:00',
      security: 'Corporate Bond XYZ Ltd 12%',
      securityId: 3,
      amount: 750000,
      price: 101.2500,
      status: 'Rejected',
      transactionCode: 'SEL',
      commission: 375.0000,
      brokerage: 187.5000,
      description: 'Portfolio rebalancing',
      remarks: 'Rejected due to price discrepancy'
    }
  ];

  const fetchTransactions = () => {
    setRecentTransactions([]); // Clear before fetching to show loading state
    setError("");

    transactionApi.getAll()
      .then(transactions => {
        console.log('Fetched transactions:', transactions);
        setRecentTransactions(transactions);
        if (!transactions || transactions.length === 0) {
          setError('No transactions found.');
        }
      })
      .catch(err => {
        console.error('Error fetching transactions:', err);
        // Use mock data when API fails and show a warning message
        setRecentTransactions(mockTransactions);
        setError('Using mock transaction data. Backend connection failed.');
      });
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Format values where needed
    if (name === 'amount' || name === 'price' || name === 'yield' || name === 'commission' || name === 'brokerage' || name === 'interestRate') {
      // Ensure values are correctly truncated to 4 decimal places when displayed
      // This follows the financial calculation requirements
      const numValue = parseFloat(value);

      setTransactionData(prev => ({
        ...prev,
        [name]: isNaN(numValue) ? '' : value
      }));
    } else {
      setTransactionData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Generate a unique deal number (DEALyyyyMMdd-xxxx)
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
    const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4-digit random
    const dealNumber = `DEAL${dateStr}-${randomSuffix}`;

    // Format financial values to ensure they are truncated to 4 decimal places
    const formattedData = {
      ...transactionData,
      deal_number: dealNumber,
      sourceAccount: transactionData.sourceAccount,
      security_id: transactionData.security,
      counterparty_id: transactionData.counterparty,
      transaction_type_id: transactionData.transactionType,
      amount: transactionData.amount ? parseFloat(parseFloat(transactionData.amount).toFixed(4)) : null,
      price: transactionData.price ? parseFloat(parseFloat(transactionData.price).toFixed(4)) : null,
      yield: transactionData.yield ? parseFloat(parseFloat(transactionData.yield).toFixed(4)) : null,
      interest_rate: transactionData.interestRate ? parseFloat(parseFloat(transactionData.interestRate).toFixed(4)) : null,
      commission: transactionData.commission ? parseFloat(parseFloat(transactionData.commission).toFixed(4)) : null,
      brokerage: transactionData.brokerage ? parseFloat(parseFloat(transactionData.brokerage).toFixed(4)) : null,
      date: transactionData.tradeDate,
      tradeDate: transactionData.tradeDate,
      valueDate: transactionData.valueDate,
      settlement_mode: transactionData.settlementMode,
      transaction_code: transactionData.transactionCode,
      initiator: loggedInUsername
    };

    transactionApi.create(formattedData)
      .then(response => {
        const transactionId = response?.id || response?.deal_number || dealNumber;
        setSuccess('Transaction submitted successfully');
        setCreatedTransactionId(transactionId);
        setShowDocumentSection(true);
        // Reset form
        setTransactionData({
          tradeDate: format(new Date(), 'yyyy-MM-dd'),
          valueDate: format(new Date(), 'yyyy-MM-dd'),
          security: '',
          sourceAccount: '',
          amount: '',
          interestRate: '',
          counterparty: '',
          transactionType: '',
          settlementMode: 'RTGS',
          price: '',
          yield: '',
          description: '',
          portfolio: 'AFS',
          strategy: '',
          currency: 'LKR',
          transactionCode: '',
          commission: '',
          brokerage: '',
          remarks: ''
        });
        // Refresh transaction list
        fetchTransactions();
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Error submitting transaction');
        console.error('Error submitting transaction:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Handle transaction authorization
  const handleAuthorize = (id, action) => {
    const authData = {
      transactionId: id,
      action: action,
      comment: comment
    };

    transactionApi.authorizeTransaction(authData)
      .then(response => {
        setSuccess(`Transaction ${action === 'APPROVE' ? 'approved' : 'rejected'} successfully`);
        // Refresh transaction list
        fetchTransactions();
      })
      .catch(err => {
        setError(err.response?.data?.message || `Error ${action === 'APPROVE' ? 'approving' : 'rejecting'} transaction`);
        console.error(`Error ${action === 'APPROVE' ? 'approving' : 'rejecting'} transaction:`, err);
      });
  };

  // Open transaction details modal
  const viewTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };

  // Open edit modal
  const openEditModal = (transaction) => {
    setEditTransaction(transaction);
    setEditForm({
      ...transaction,
      tradeDate: transaction.tradeDate ? format(new Date(transaction.tradeDate), 'yyyy-MM-dd') : '',
      valueDate: transaction.valueDate ? format(new Date(transaction.valueDate), 'yyyy-MM-dd') : ''
    });
  };

  // Clean UI styles specific to TransactionPage only
  const transactionPageStyles = {
    pageContainer: {
      minHeight: '100vh',
      background: '#f5f8fa',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px 20px 20px 10px',
      marginLeft: '0',
      color: '#000000',
      boxSizing: 'border-box',
      alignItems: 'flex-start',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    },
    formLabel: {
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#000000',
      marginBottom: '6px',
      display: 'block'
    },
    formControl: {
      padding: '10px 12px',
      border: '1px solid #d0d0d0',
      borderRadius: '6px',
      fontSize: '0.95rem',
      background: '#ffffff',
      color: '#000000',
      width: '100%',
      boxSizing: 'border-box',
      height: '40px',
      transition: 'all 0.2s ease'
    },
    formControlFocus: {
      borderColor: '#2563eb',
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)'
    },
    dateInput: {
      backgroundColor: '#ffffff',
      border: '1px solid #d0d0d0',
      color: '#000000'
    },
    container: {
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      padding: '28px 32px 28px 20px',
      marginBottom: '24px',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      overflow: 'hidden'
    },
    heading: {
      color: '#1a237e',
      marginBottom: '24px',
      fontSize: '1.5rem',
      fontWeight: 600,
      textAlign: 'left',
      paddingBottom: '16px',
      borderBottom: '3px solid #2563eb'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '20px',
      marginBottom: '20px',
      alignItems: 'start',
      justifyItems: 'start',
      width: '100%'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      boxSizing: 'border-box',
      alignSelf: 'start',
      justifySelf: 'start'
    },
    compactFormGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      width: '100%',
      boxSizing: 'border-box'
    }
  };

  return (
    <div className="page-container" style={transactionPageStyles.pageContainer}>
      {/* Transaction Details Modal */}
      {showDetails && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Transaction Details</h3>
            <form className="compact-form-grid" style={{ pointerEvents: 'none', opacity: 1 }}>
              {/* Row 1 */}
              <div className="form-row">
                {/* Trade Date */}
                <div className="form-group">
                  <label className="form-label">Trade Date</label>
                  <input readOnly type="date" value={selectedTransaction.tradeDate || selectedTransaction.trade_date ? format(new Date(selectedTransaction.tradeDate || selectedTransaction.trade_date), 'yyyy-MM-dd') : ''} className="form-control" />
                </div>
                {/* Value Date */}
                <div className="form-group">
                  <label className="form-label">Value Date</label>
                  <input readOnly type="date" value={selectedTransaction.valueDate || selectedTransaction.value_date ? format(new Date(selectedTransaction.valueDate || selectedTransaction.value_date), 'yyyy-MM-dd') : ''} className="form-control" />
                </div>
                {/* Security */}
                <div className="form-group">
                  <label className="form-label">Security</label>
                  <select disabled value={selectedTransaction.security || selectedTransaction.security_id || ''} className="form-control">
                    <option value="">Select Security</option>
                    {securities.map(security => (
                      <option key={security.id} value={security.id}>{security.name}</option>
                    ))}
                  </select>
                </div>
                {/* Source Account */}
                <div className="form-group">
                  <label className="form-label">Source Account</label>
                  <select disabled value={selectedTransaction.sourceAccount || selectedTransaction.source_account_id || ''} className="form-control">
                    <option value="">Select Account</option>
                    {sourceAccounts.map(account => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 2 */}
              <div className="form-row">
                {/* Amount */}
                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <input readOnly type="number" value={formatPrice(selectedTransaction.amount, 4)} className="form-control" />
                </div>
                {/* Interest Rate */}
                <div className="form-group">
                  <label className="form-label">Interest Rate (%)</label>
                  <input readOnly type="number" value={formatPrice(selectedTransaction.interestRate || selectedTransaction.interest_rate, 4)} className="form-control" />
                </div>
                {/* Counterparty */}
                <div className="form-group">
                  <label className="form-label">Counterparty</label>
                  <select disabled value={selectedTransaction.counterparty || selectedTransaction.counterparty_id || ''} className="form-control">
                    <option value="">Select Counterparty</option>
                    {counterparties.map(cp => (
                      <option key={cp.unique_id} value={cp.unique_id}>
                        {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Transaction Type */}
                <div className="form-group">
                  <label className="form-label">Transaction Type</label>
                  <select disabled value={selectedTransaction.transactionType || selectedTransaction.transaction_type_id || ''} className="form-control">
                    <option value="">Select Type</option>
                    {transactionTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 3 */}
              <div className="form-row">
                {/* Settlement Mode */}
                <div className="form-group">
                  <label className="form-label">Settlement Mode</label>
                  <select disabled value={selectedTransaction.settlementMode || selectedTransaction.settlement_mode || ''} className="form-control">
                    <option value="">Select Mode</option>
                    {settlementModes.map((mode, index) => (
                      <option key={index} value={mode.id}>{mode.name}</option>
                    ))}
                  </select>
                </div>
                {/* Price */}
                <div className="form-group">
                  <label className="form-label">Price</label>
                  <input readOnly type="number" value={formatPrice(selectedTransaction.price, 4)} className="form-control" />
                </div>
                {/* Yield */}
                <div className="form-group">
                  <label className="form-label">Yield (%)</label>
                  <input readOnly type="number" value={formatPrice(selectedTransaction.yield, 4)} className="form-control" />
                </div>
                {/* Portfolio */}
                <div className="form-group">
                  <label className="form-label">Portfolio</label>
                  <select disabled value={selectedTransaction.portfolio || ''} className="form-control">
                    <option value="">Select Portfolio</option>
                    {portfolioOptions.map((portfolio, index) => (
                      <option key={index} value={portfolio.id}>{portfolio.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 4 */}
              <div className="form-row">
                {/* Strategy */}
                <div className="form-group">
                  <label className="form-label">Strategy</label>
                  <input readOnly type="text" value={selectedTransaction.strategy || ''} className="form-control" disabled />
                </div>
                {/* Currency */}
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select disabled value={selectedTransaction.currency || ''} className="form-control">
                    <option value="">Select Currency</option>
                    {currencyOptions.map((currency, index) => (
                      <option key={index} value={currency.id}>{currency.name}</option>
                    ))}
                  </select>
                </div>
                {/* Transaction Code */}
                <div className="form-group">
                  <label className="form-label">Transaction Code</label>
                  <input readOnly type="text" value={selectedTransaction.transactionCode || selectedTransaction.transaction_code || ''} className="form-control" />
                </div>
                {/* Commission */}
                <div className="form-group">
                  <label className="form-label">Commission</label>
                  <input readOnly type="number" value={selectedTransaction.commission ? Number(selectedTransaction.commission).toFixed(4) : ''} className="form-control" />
                </div>
              </div>
              {/* Row 5 */}
              <div className="form-row">
                {/* Brokerage */}
                <div className="form-group">
                  <label className="form-label">Brokerage</label>
                  <input readOnly type="number" value={selectedTransaction.brokerage ? Number(selectedTransaction.brokerage).toFixed(4) : ''} className="form-control" />
                </div>
                {/* Description */}
                <div className="form-group description-group">
                  <label className="form-label">Description</label>
                  <textarea readOnly value={selectedTransaction.description || ''} className="form-control" rows="2"></textarea>
                </div>
                {/* Remarks */}
                <div className="form-group remarks-group">
                  <label className="form-label">Remarks</label>
                  <textarea readOnly value={selectedTransaction.remarks || ''} className="form-control" rows="2"></textarea>
                </div>
              </div>
            </form>
            <button className="btn" style={{ marginTop: '16px', pointerEvents: 'auto' }} onClick={() => setShowDetails(false)}>Close</button>
          </div>
        </div>
      )}

      <div className="content" style={{width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', boxSizing: 'border-box', paddingLeft: '0', marginLeft: '0', overflow: 'hidden'}}>
        {/* Transaction form for all users */}
        <div className="transactions-table-container" style={{...transactionPageStyles.container, width: '100%', maxWidth: '100%', marginLeft: '0', overflow: 'hidden'}}>
          <h2 style={transactionPageStyles.heading}>Submit New Transaction</h2>
          <form onSubmit={handleSubmit} style={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
            <div className="compact-form-grid" style={{...transactionPageStyles.compactFormGrid, width: '100%'}}>
              {/* Row 1 */}
              <div className="form-row" style={transactionPageStyles.formRow}>
                {/* Trade Date */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Trade Date</label>
                  <input
                    type="date"
                    name="tradeDate"
                    value={transactionData.tradeDate}
                    onChange={handleChange}
                    className="form-control"
                    style={{...transactionPageStyles.formControl, ...transactionPageStyles.dateInput}}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    required
                  />
                </div>
                {/* Value Date */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Value Date</label>
                  <input
                    type="date"
                    name="valueDate"
                    value={transactionData.valueDate}
                    onChange={handleChange}
                    className="form-control"
                    style={{...transactionPageStyles.formControl, ...transactionPageStyles.dateInput}}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    required
                  />
                </div>
                {/* Security */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Security</label>
                  <select
                    name="security"
                    value={transactionData.security}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    required
                  >
                    <option value="">Select Security</option>
                    {securities.map(security => (
                      <option key={security.id} value={security.id}>
                        {security.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Source Account */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Source Account</label>
                  <select
                    name="sourceAccount"
                    value={transactionData.sourceAccount}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  >
                    <option value="">Select Account</option>
                    {sourceAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 2 */}
              <div className="form-row" style={transactionPageStyles.formRow}>
                {/* Amount */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Amount</label>
                  <input
                    type="number"
                    name="amount"
                    value={transactionData.amount || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    step="0.0001"
                  />
                </div>
                {/* Interest Rate */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Interest Rate (%)</label>
                  <input
                    type="number"
                    name="interestRate"
                    value={transactionData.interestRate || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    step="0.0001"
                  />
                </div>
                {/* Counterparty */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Counterparty</label>
                  <select
                    name="counterparty"
                    value={transactionData.counterparty}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  >
                    <option value="">Select Counterparty</option>
                    {counterparties.map(cp => (
                      <option key={cp.unique_id} value={cp.unique_id}>
                        {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Transaction Type */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Transaction Type</label>
                  <select
                    name="transactionType"
                    value={transactionData.transactionType}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    required
                  >
                    <option value="">Select Type</option>
                    {transactionTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 3 */}
              <div className="form-row" style={transactionPageStyles.formRow}>
                {/* Settlement Mode */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Settlement Mode</label>
                  <select
                    name="settlementMode"
                    value={transactionData.settlementMode}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  >
                    <option value="">Select Mode</option>
                    {settlementModes.map((mode, index) => (
                      <option key={index} value={mode.id}>
                        {mode.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Price */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Price</label>
                  <input
                    type="number"
                    name="price"
                    value={transactionData.price || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    step="0.0001"
                  />
                </div>
                {/* Yield */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Yield (%)</label>
                  <input
                    type="number"
                    name="yield"
                    value={transactionData.yield || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    step="0.0001"
                  />
                </div>
                {/* Portfolio */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Portfolio</label>
                  <select
                    name="portfolio"
                    value={transactionData.portfolio}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  >
                    <option value="">Select Portfolio</option>
                    {portfolioOptions.map((portfolio, index) => (
                      <option key={index} value={portfolio.id}>
                        {portfolio.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 4 */}
              <div className="form-row" style={transactionPageStyles.formRow}>
                {/* Strategy */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Strategy</label>
                  <select
                    name="strategy"
                    value={transactionData.strategy || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  >
                    <option value="">Select Strategy</option>
                    {strategies.map(strategy => (
                      <option key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Currency */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Currency</label>
                  <select
                    name="currency"
                    value={transactionData.currency}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  >
                    <option value="">Select Currency</option>
                    {currencyOptions.map((currency, index) => (
                      <option key={index} value={currency.id}>
                        {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Transaction Code */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Transaction Code</label>
                  <input
                    type="text"
                    name="transactionCode"
                    value={transactionData.transactionCode || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                  />
                </div>
                {/* Commission */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Commission</label>
                  <input
                    type="number"
                    name="commission"
                    value={transactionData.commission || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    step="0.0001"
                  />
                </div>
              </div>
              {/* Row 5 */}
              <div className="form-row" style={transactionPageStyles.formRow}>
                {/* Brokerage */}
                <div className="form-group" style={transactionPageStyles.formGroup}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Brokerage</label>
                  <input
                    type="number"
                    name="brokerage"
                    value={transactionData.brokerage || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={transactionPageStyles.formControl}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    step="0.0001"
                  />
                </div>
                {/* Description */}
                <div className="form-group description-group" style={{...transactionPageStyles.formGroup, gridColumn: 'span 2'}}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Description</label>
                  <textarea
                    name="description"
                    value={transactionData.description || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={{...transactionPageStyles.formControl, minHeight: '80px', height: 'auto'}}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    rows="2"
                  ></textarea>
                </div>
                {/* Remarks */}
                <div className="form-group remarks-group" style={{...transactionPageStyles.formGroup, gridColumn: 'span 1'}}>
                  <label className="form-label" style={transactionPageStyles.formLabel}>Remarks</label>
                  <textarea
                    name="remarks"
                    value={transactionData.remarks || ''}
                    onChange={handleChange}
                    className="form-control"
                    style={{...transactionPageStyles.formControl, minHeight: '80px', height: 'auto'}}
                    onFocus={(e) => Object.assign(e.target.style, transactionPageStyles.formControlFocus)}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    rows="2"
                  ></textarea>
                </div>
              </div>
              {/* Error and success messages */}
              <div className="form-row alert-row" style={{...transactionPageStyles.formRow, marginTop: '16px'}}>
                {error && (
                  <div className="alert alert-danger" style={{
                    padding: '12px 16px',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    fontSize: '0.9rem',
                    backgroundColor: '#fee2e2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca'
                  }}>
                    {error}
                  </div>
                )}
                {success && (
                  <div className="alert alert-success" style={{
                    padding: '12px 16px',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    fontSize: '0.9rem',
                    backgroundColor: '#d1fae5',
                    color: '#047857',
                    border: '1px solid #a7f3d0'
                  }}>
                    {success}
                  </div>
                )}
                <div className="button-container" style={{marginLeft: 'auto'}}>
                  <button
                    type="submit"
                    className="save-verify-button"
                    disabled={isLoading}
                    style={{
                      background: isLoading ? '#93c5fd' : '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 24px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
                    }}
                    onMouseEnter={(e) => !isLoading && (e.target.style.background = '#1d4ed8')}
                    onMouseLeave={(e) => !isLoading && (e.target.style.background = '#2563eb')}
                  >
                    {isLoading ? 'Processing...' : 'Save and Verify'}
                    {!isLoading && <span className="tick-icon">✓</span>}
                  </button>
                </div>
              </div>
            </div>
          </form>
          
          {/* Document Upload Section - Show after transaction is created */}
          {showDocumentSection && createdTransactionId && (
            <div style={{ marginTop: '24px', width: '100%' }}>
              <DocumentUpload
                transactionType="transaction"
                transactionId={createdTransactionId}
                onUploadSuccess={(documents) => {
                  setSuccess('Document(s) uploaded successfully');
                  setShowDocumentSection(false); // Hide upload after successful upload
                }}
                onUploadError={(error) => {
                  setError('Failed to upload document: ' + (error.response?.data?.error || error.message));
                }}
                multiple={true}
              />
              <DocumentList
                transactionType="transaction"
                transactionId={createdTransactionId}
                onDocumentDeleted={() => {
                  // Optionally refresh or show message
                }}
              />
            </div>
          )}
        </div>
      </div>
      {/* Transaction list section */}
      <div className="transactions-section" style={{...transactionPageStyles.container, width: '100%', maxWidth: '100%', alignSelf: 'stretch'}}>
        <h2 style={transactionPageStyles.heading}>Recent Transactions</h2>
        {recentTransactions.length === 0 ? (
          <div className="no-transactions-message">
            {isLoading ? 'Loading transactions...' : 'No transactions found.'}
          </div>
        ) : (
          <div className="transactions-table-wrapper">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Deal Number</th>
                  <th>Trade Date</th>
                  <th>Value Date</th>
                  <th>Security</th>
                  <th>Amount</th>
                  <th>Price</th>
                  <th>Yield (%)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id || transaction._id}>
                    <td>{transaction.deal_number || 'N/A'}</td>
                    <td>
                      {transaction.tradeDate || transaction.trade_date
                        ? format(new Date(transaction.tradeDate || transaction.trade_date), 'yyyy-MM-dd')
                        : 'N/A'}
                    </td>
                    <td>
                      {transaction.valueDate || transaction.value_date
                        ? format(new Date(transaction.valueDate || transaction.value_date), 'yyyy-MM-dd')
                        : 'N/A'}
                    </td>
                    <td className="numeric-cell">
                      {transaction.amount ? formatPrice(transaction.amount) : 'N/A'}
                    </td>
                    <td className="numeric-cell">
                      {transaction.price ? formatPrice(transaction.price) : 'N/A'}
                    </td>
                    <td className="numeric-cell">
                      {transaction.yield ? formatPrice(transaction.yield) : 'N/A'}
                    </td>
                    <td className={`status-cell status-${transaction.status?.toLowerCase() || 'pending'}`}>
                      {transaction.status || 'Pending'}
                    </td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => viewTransactionDetails(transaction)}
                        title="View Details"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionPage;
